import { useMutation } from "@tanstack/react-query"
import type {
  UseMutationOptions,
  UseMutationResult,
} from "@tanstack/react-query"

import client from "~/backend/axiosClient"

import type {
  ExportProductQrCodeMutationRequest,
  ExportProductQrCodeMutationResponse,
} from "../types/ExportProductQrCode"

type ExportProductQrCodeClient = typeof client<
  ExportProductQrCodeMutationResponse,
  Error,
  ExportProductQrCodeMutationRequest
>
type ExportProductQrCode = {
  data: ExportProductQrCodeMutationResponse
  error: Error
  request: ExportProductQrCodeMutationRequest
  pathParams: never
  queryParams: never
  headerParams: never
  response: Awaited<ReturnType<ExportProductQrCodeClient>>
  client: {
    parameters: Partial<Parameters<ExportProductQrCodeClient>[0]>
    return: Awaited<ReturnType<ExportProductQrCodeClient>>
  }
}
/**
 * @summary Export product QR code
 * @link /v1/product/export-product-qr-code
 */
export function useExportProductQrCode(
  options: {
    mutation?: UseMutationOptions<
      ExportProductQrCode["response"],
      ExportProductQrCode["error"],
      ExportProductQrCode["request"]
    >
    client?: ExportProductQrCode["client"]["parameters"]
  } = {}
): UseMutationResult<
  ExportProductQrCode["response"],
  ExportProductQrCode["error"],
  ExportProductQrCode["request"]
> {
  const { mutation: mutationOptions, client: clientOptions = {} } =
    options ?? {}
  return useMutation<
    ExportProductQrCode["response"],
    ExportProductQrCode["error"],
    ExportProductQrCode["request"]
  >({
    mutationFn: async (data) => {
      const res = await client<
        ExportProductQrCode["data"],
        ExportProductQrCode["error"],
        ExportProductQrCode["request"]
      >({
        method: "post",
        url: `/v1/product/export-product-qr-code`,
        data,
        ...clientOptions,
      })
      return res
    },
    ...mutationOptions,
  })
}
